import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import { getHeaderConfig } from '../Users/Users';
import axios from 'axios';
import { api } from '../Users/user';

declare global {
  interface Window {
    config?: { whatsappApiUrl?: string; whatsappApiKey?: string; whatsappSession?: string };
  }
}

const API_BASE_URL = api + '/api/whatsapp';
const API_KEY = window?.config?.whatsappApiKey || '';
const SESSION_DEFAULT = window?.config?.whatsappSession || 'med_session';

const BotConnection = ({ servicesManager }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();
  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  const [sessionName, setSessionName] = useState(SESSION_DEFAULT);
  const [status, setStatus] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Hola desde OHIF Viewer Medservice');
  const [msgResult, setMsgResult] = useState('');
  const [sending, setSending] = useState(false);

  const getHeaders = () => ({
    headers: API_KEY ? { 'x-api-key': API_KEY } : {},
  });

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/sessions/${sessionName}/status`, getHeaders());
      const info = res.data?.data || {};
      setStatus(info.status || 'DISCONNECTED');
      if (info.qrBase64) {
        setQrCode(info.qrBase64);
      } else {
        setQrCode('');
      }
    } catch {
      setStatus('DISCONNECTED');
    }
  };

  const startSession = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/sessions/init`, { session: sessionName }, getHeaders());
      setTimeout(checkStatus, 2000);
    } catch (err) {
      toast.error('Error al iniciar la sesión. Verifica que la API de WhatsApp esté corriendo.');
    } finally {
      setLoading(false);
    }
  };

  const logoutSession = async () => {
    if (window.confirm('¿Seguro que deseas desconectar?')) {
      await axios.delete(`${API_BASE_URL}/sessions/${sessionName}/logout`, getHeaders()).catch(() => null);
      setStatus('DISCONNECTED');
      setQrCode('');
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setMsgResult('');
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (API_KEY) headers['x-api-key'] = API_KEY;
      const res = await axios.post(
        `${API_BASE_URL}/messages/send`,
        { session: sessionName, to: testPhone, message: testMessage },
        { headers }
      );
      if (res.data?.success) {
        setMsgResult('ok');
        toast.success(' Mensaje enviado correctamente');
      } else throw new Error();
    } catch {
      setMsgResult('error');
      toast.error('❌ Error al enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 4000);
    return () => clearInterval(interval);
  }, [sessionName]);

  const statusBadge = () => {
    if (status === 'READY') return <span className="rounded-full bg-green-700 px-3 py-0.5 text-xs font-semibold">Conectado</span>;
    if (status === 'QR_READY') return <span className="rounded-full bg-yellow-700 px-3 py-0.5 text-xs font-semibold">Esperando QR</span>;
    return <span className="rounded-full bg-red-800 px-3 py-0.5 text-xs font-semibold">{status || 'Desconectado'}</span>;
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Título */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">WhatsApp – Conexión del Bot</h1>
            <p className="mt-1 text-gray-400">Gestiona la sesión y envía mensajes de prueba</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Panel Estado y QR */}
          <div className="rounded-lg border border-gray-700 bg-secondary-dark p-6">
            <h2 className="mb-4 text-lg font-semibold">Estado de la Sesión</h2>

            <div className="mb-4">
              <label className="mb-1 block text-sm text-gray-400">Nombre de Sesión</label>
              <input
                type="text"
                value={sessionName}
                onChange={e => setSessionName(e.target.value)}
                className="w-full rounded border border-gray-700 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="mb-5 flex items-center gap-3">
              <span className="text-sm text-gray-400">Estado:</span>
              {statusBadge()}
            </div>

            {/* QR Code */}
            {status === 'QR_READY' && qrCode && (
              <div className="mb-4 rounded-lg bg-white p-4 text-center">
                <p className="mb-2 font-semibold text-gray-800">📱 Escanea con WhatsApp</p>
                <img
                  src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="WhatsApp QR Code"
                  className="mx-auto block max-w-[220px]"
                />
              </div>
            )}

            {status === 'QR_READY' && !qrCode && (
              <p className="mb-4 text-sm text-yellow-400">⏳ Generando QR... se mostrará automáticamente.</p>
            )}

            {status === 'READY' && (
              <div className="mb-4 rounded-lg border border-green-700 bg-green-900/20 p-3 text-sm text-green-400">
                Bot conectado y listo para enviar mensajes
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={startSession}
                disabled={loading || status === 'READY'}
                className="flex flex-1 items-center justify-center gap-2 rounded bg-blue-600 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? '⏳ Iniciando...' : 'Conectar'}
              </button>
              <button
                onClick={logoutSession}
                disabled={status === 'DISCONNECTED' || status === 'NOT_FOUND'}
                className="flex flex-1 items-center justify-center rounded bg-red-700 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Desconectar
              </button>
            </div>
          </div>

          {/* Panel Prueba de Mensajes */}
          <div className="rounded-lg border border-gray-700 bg-secondary-dark p-6">
            <h2 className="mb-4 text-lg font-semibold">Prueba de Envío</h2>
            <form onSubmit={handleSendMessage} className="flex flex-col gap-4">
              <div>
                <label className="mb-1 block text-sm text-gray-400">Número (con código de país, ej: +59171234567)</label>
                <input
                  type="text"
                  placeholder="+591 71234567"
                  value={testPhone}
                  onChange={e => setTestPhone(e.target.value)}
                  required
                  className="w-full rounded border border-gray-700 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-400">Mensaje</label>
                <textarea
                  value={testMessage}
                  onChange={e => setTestMessage(e.target.value)}
                  required
                  rows={4}
                  className="w-full resize-y rounded border border-gray-700 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={status !== 'READY' || sending}
                className="flex w-full items-center justify-center rounded bg-green-700 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? 'Enviando...' : status !== 'READY' ? 'Conecta el bot primero' : '📩 Enviar Mensaje de Prueba'}
              </button>

              {msgResult === 'error' && (
                <div className="rounded border border-red-700 bg-red-900/20 p-3 text-center text-sm text-red-400">
                  ❌ No se pudo enviar el mensaje
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotConnection;
